import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Users, FileText, TrendingUp, Lock, Download, Mail, Calendar, Crown, Zap, Upload, CheckCircle, AlertCircle, Activity, CreditCard, Database, Clock, Send, Edit2, X, IndianRupee, ShoppingCart, CalendarDays, ChevronLeft, ChevronRight, Megaphone, Radio, Eye, History, Ticket, InboxIcon, CheckCheck } from "lucide-react";
// No external WYSIWYG library - using custom contentEditable editor
import axios from "axios";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('admin_authenticated') === 'true';
  });
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [adminKey, setAdminKey] = useState(() => {
    return localStorage.getItem('admin_key') || "";
  });
  const [csvFile, setCsvFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [bulkEmailResult, setBulkEmailResult] = useState(null);
  const [remainingEmailLoading, setRemainingEmailLoading] = useState(false);
  const [remainingEmailResult, setRemainingEmailResult] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [fixingPasswords, setFixingPasswords] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lmsCredits, setLmsCredits] = useState(null);
  
  // Email edit state
  const [editingEmailUserId, setEditingEmailUserId] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailUpdateLoading, setEmailUpdateLoading] = useState(false);
  
  // Sales Dashboard state
  const [salesStats, setSalesStats] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateSales, setSelectedDateSales] = useState(null);
  
  // App Settings state
  const [appSettings, setAppSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Rich text editor ref
  const editorRef = useRef(null);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastSegment, setBroadcastSegment] = useState("both");
  const [broadcastPersonalize, setBroadcastPersonalize] = useState(true);
  const [broadcastSchedule, setBroadcastSchedule] = useState("");
  const [broadcastPreviewCount, setBroadcastPreviewCount] = useState(null);
  const [broadcastPreviewLoading, setBroadcastPreviewLoading] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastJobId, setBroadcastJobId] = useState(null);
  const [broadcastJobStatus, setBroadcastJobStatus] = useState(null);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [broadcastHistoryLoading, setBroadcastHistoryLoading] = useState(false);
  const [showBroadcastHistory, setShowBroadcastHistory] = useState(false);

  // Support Tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsFilter, setTicketsFilter] = useState("open"); // open | resolved | all
  const [resolvingTicket, setResolvingTicket] = useState(null);
  const [replyingTicket, setReplyingTicket] = useState(null); // ticket_id with open reply box
  const [replyTexts, setReplyTexts] = useState({}); // { [ticket_id]: string }
  const [sendingReply, setSendingReply] = useState(null);

  const filteredStudents = students.filter(s => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (s.name || "").toLowerCase().includes(q) ||
           (s.mobile || "").includes(q) ||
           (s.email || "").toLowerCase().includes(q);
  });

  // Auto-fetch data on mount if already authenticated
  useEffect(() => {
    const storedKey = localStorage.getItem('admin_key');
    if (storedKey && isAuthenticated) {
      fetchData(storedKey);
      fetchLmsCredits();
      fetchSalesStats(storedKey);
      fetchCalendarData(storedKey, calendarMonth, calendarYear);
      fetchAppSettings(storedKey);
      // Load support tickets automatically
      setTimeout(() => fetchSupportTickets("open"), 500);
    }
  }, []);

  const fetchLmsCredits = async () => {
    try {
      const response = await axios.get(`${API}/notes/lms/credits-summary`);
      setLmsCredits(response.data);
    } catch (error) {
      console.log("Failed to fetch LMS credits");
    }
  };
  
  // App Settings Functions
  const fetchAppSettings = async (key) => {
    setSettingsLoading(true);
    try {
      const response = await axios.get(`${API}/settings/settings?admin_key=${key}`);
      setAppSettings(response.data);
    } catch (error) {
      console.log("Failed to fetch app settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateAppSettings = async () => {
    if (!appSettings) return;
    
    setSavingSettings(true);
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      formData.append('running_header_text', appSettings.running_header_text || '');
      formData.append('running_header_enabled', appSettings.running_header_enabled || false);
      formData.append('free_descriptive_limit', appSettings.free_descriptive_limit);
      formData.append('paid_descriptive_limit', appSettings.paid_descriptive_limit);
      formData.append('unlimited_descriptive_limit', appSettings.unlimited_descriptive_limit);
      formData.append('free_mcq_per_topic', appSettings.free_mcq_per_topic);
      formData.append('paid_mcq_per_topic', appSettings.paid_mcq_per_topic);
      formData.append('unlimited_mcq_per_topic', appSettings.unlimited_mcq_per_topic);
      formData.append('mcq_batch_size', appSettings.mcq_batch_size);
      formData.append('include_graphs_for_economics', appSettings.include_graphs_for_economics);
      formData.append('pdf_retention_days', appSettings.pdf_retention_days);
      
      const response = await axios.post(`${API}/settings/settings/update`, formData);
      setAppSettings(response.data.settings);
      toast.success("Settings updated successfully!");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };


  // Sales Dashboard Functions
  const fetchSalesStats = async (key) => {
    setSalesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sales/stats?admin_key=${key}`);
      setSalesStats(response.data);
    } catch (error) {
      console.log("Failed to fetch sales stats");
    } finally {
      setSalesLoading(false);
    }
  };

  const fetchCalendarData = async (key, month, year) => {
    try {
      const response = await axios.get(`${API}/admin/sales/calendar?admin_key=${key}&month=${month}&year=${year}`);
      setCalendarData(response.data);
    } catch (error) {
      console.log("Failed to fetch calendar data");
    }
  };

  const fetchSalesByDate = async (date) => {
    try {
      const response = await axios.get(`${API}/admin/sales/by-date?admin_key=${adminKey}&date=${date}`);
      setSelectedDateSales(response.data);
    } catch (error) {
      console.log("Failed to fetch sales by date");
    }
  };

  const handleCalendarMonthChange = (direction) => {
    let newMonth = calendarMonth + direction;
    let newYear = calendarYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setCalendarMonth(newMonth);
    setCalendarYear(newYear);
    fetchCalendarData(adminKey, newMonth, newYear);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    fetchSalesByDate(date);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const renderCalendar = () => {
    if (!calendarData) return null;
    
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Add cells for each day
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData.daily_data[dateStr];
      const hasSales = dayData && dayData.count > 0;
      const isSelected = selectedDate === dateStr;
      const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
      
      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(dateStr)}
          className={`p-2 text-center cursor-pointer rounded-lg transition-all ${
            isSelected ? 'bg-blue-600 text-white' :
            isToday ? 'bg-purple-600/30 border border-purple-500' :
            hasSales ? 'bg-green-600/20 hover:bg-green-600/40' : 'hover:bg-white/10'
          }`}
        >
          <div className="text-sm font-medium">{day}</div>
          {hasSales && (
            <div className="text-xs text-green-400 mt-1">
              ₹{dayData.revenue}
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  const handleFixPasswords = async () => {
    if (!window.confirm(`This will set default passwords (last 4 digits + "lrs") for all users who don't have passwords.\n\nProceed?`)) {
      return;
    }
    
    setFixingPasswords(true);
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      
      const response = await axios.post(`${API}/admin/fix-passwords`, formData);
      toast.success(`Fixed ${response.data.fixed} user passwords!`);
      fetchData(adminKey);
    } catch (error) {
      toast.error("Failed to fix passwords");
    } finally {
      setFixingPasswords(false);
    }
  };

  const handleEmailEdit = (userId, currentEmail) => {
    setEditingEmailUserId(userId);
    setNewEmail(currentEmail || "");
  };

  const handleEmailCancel = () => {
    setEditingEmailUserId(null);
    setNewEmail("");
  };

  const handleEmailUpdate = async (userId) => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setEmailUpdateLoading(true);
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      formData.append('user_id', userId);
      formData.append('new_email', newEmail.trim());

      const response = await axios.post(`${API}/admin/update-user-email`, formData);
      toast.success(`Email updated successfully!`);
      
      // Update local state
      setStudents(prev => prev.map(s => 
        s.id === userId ? { ...s, email: newEmail.trim() } : s
      ));
      
      setEditingEmailUserId(null);
      setNewEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setEmailUpdateLoading(false);
    }
  };

  const fetchEmailStats = async (key) => {
    try {
      const response = await axios.get(`${API}/admin/email-stats?admin_key=${key}`);
      setEmailStats(response.data);
    } catch (error) {
      console.log("Failed to fetch email stats");
    }
  };

  // Broadcast email functions
  const fetchBroadcastPreview = async () => {
    if (!adminKey) return;
    setBroadcastPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append("admin_key", adminKey);
      formData.append("segment", broadcastSegment);
      const res = await axios.post(`${API}/admin/broadcast-email/preview`, formData);
      setBroadcastPreviewCount(res.data.count);
    } catch (err) {
      toast.error("Preview failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setBroadcastPreviewLoading(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastSubject.trim()) { toast.error("Subject is required"); return; }
    if (!broadcastBody.trim() || broadcastBody === "<p><br></p>") { toast.error("Email body is required"); return; }
    if (!window.confirm(`Send broadcast to "${broadcastSegment}" students?\n\nSubject: ${broadcastSubject}\n\nThis cannot be undone.`)) return;

    setBroadcastSending(true);
    try {
      const formData = new FormData();
      formData.append("admin_key", adminKey);
      formData.append("subject", broadcastSubject);
      formData.append("body_html", broadcastBody);
      formData.append("segment", broadcastSegment);
      formData.append("personalize", broadcastPersonalize);
      formData.append("scheduled_at", broadcastSchedule || "");
      const res = await axios.post(`${API}/admin/broadcast-email/send`, formData);
      setBroadcastJobId(res.data.job_id);
      setBroadcastJobStatus({ status: "queued", total: broadcastPreviewCount || 0, sent: 0, failed: 0, processed: 0 });
      toast.success(broadcastSchedule ? "Broadcast scheduled!" : "Broadcast started! Tracking progress below.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setBroadcastSending(false);
    }
  };

  const fetchBroadcastHistory = async () => {
    setBroadcastHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/broadcast-email/history?admin_key=${adminKey}&limit=20`);
      setBroadcastHistory(res.data.broadcasts || []);
    } catch (err) {
      toast.error("Could not load history");
    } finally {
      setBroadcastHistoryLoading(false);
    }
  };

  const fetchSupportTickets = async (status = ticketsFilter, key = adminKey) => {
    setTicketsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/support-tickets?admin_key=${key}&status=${status}&limit=100`);
      setSupportTickets(res.data.tickets || []);
    } catch (err) {
      toast.error("Could not load tickets");
    } finally {
      setTicketsLoading(false);
    }
  };

  const resolveTicket = async (ticketId) => {
    setResolvingTicket(ticketId);
    try {
      const formData = new FormData();
      formData.append("admin_key", adminKey);
      formData.append("admin_notes", "");
      await axios.post(`${API}/admin/support-tickets/${ticketId}/resolve`, formData);
      toast.success("Ticket marked as resolved");
      fetchSupportTickets(ticketsFilter);
    } catch (err) {
      toast.error("Failed to resolve ticket");
    } finally {
      setResolvingTicket(null);
    }
  };

  const sendReply = async (ticketId) => {
    const text = (replyTexts[ticketId] || "").trim();
    if (!text) return;
    setSendingReply(ticketId);
    try {
      await axios.post(`${API}/admin/support-tickets/${ticketId}/reply`, {
        admin_key: adminKey,
        reply: text,
      });
      toast.success("Reply sent! Student will see it when they open the Help bot.");
      setReplyTexts(prev => ({ ...prev, [ticketId]: "" }));
      setReplyingTicket(null);
      fetchSupportTickets(ticketsFilter);
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(null);
    }
  };

  const [emailJobId, setEmailJobId] = useState(null);
  const [emailJobStatus, setEmailJobStatus] = useState(null);

  // Poll email job status while processing
  useEffect(() => {
    if (!emailJobId || !adminKey) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/email-job-status/${emailJobId}?admin_key=${adminKey}`);
        setEmailJobStatus(res.data);
        if (['completed', 'failed'].includes(res.data.status)) {
          clearInterval(interval);
          fetchEmailStats(adminKey);
          if (res.data.status === 'completed') {
            toast.success(`Emails done! Sent: ${res.data.sent || 0}, Failed: ${res.data.failed || 0}`);
          }
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [emailJobId, adminKey]);

  // Poll broadcast job status while processing
  useEffect(() => {
    if (!broadcastJobId || !adminKey) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/email-job-status/${broadcastJobId}?admin_key=${adminKey}`);
        setBroadcastJobStatus(res.data);
        if (['completed', 'failed'].includes(res.data.status)) {
          clearInterval(interval);
          fetchBroadcastHistory();
          if (res.data.status === 'completed') {
            toast.success(`Broadcast done! Sent: ${res.data.sent || 0}, Failed: ${res.data.failed || 0}`);
          }
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [broadcastJobId, adminKey]);

  const handleBulkEmail = async () => {
    if (!window.confirm(`Queue bulk welcome emails to ALL students?\n\nThe email_worker will process them in background. You can track progress live.`)) return;
    setBulkEmailLoading(true);
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      const response = await axios.post(`${API}/admin/send-bulk-emails`, formData);
      setEmailJobId(response.data.job_id);
      setEmailJobStatus({ status: 'queued', total: response.data.total_users });
      toast.success(`Email job queued! Job ID: ${response.data.job_id}. Tracking progress below.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to queue email job");
    } finally {
      setBulkEmailLoading(false);
    }
  };

  const handleRemainingEmail = async () => {
    if (!window.confirm(`Queue welcome emails to students who haven't received one yet?\n\nWorker will process in background with live progress.`)) return;
    setRemainingEmailLoading(true);
    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      const response = await axios.post(`${API}/admin/send-remaining-emails`, formData);
      setEmailJobId(response.data.job_id);
      setEmailJobStatus({ status: 'queued', total: response.data.remaining_users });
      toast.success(`Email job queued! ${response.data.remaining_users} in queue. Tracking progress below.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to queue email job");
    } finally {
      setRemainingEmailLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('password', password);
      
      const response = await axios.post(`${API}/admin/login`, formData);
      if (response.data.success) {
        setIsAuthenticated(true);
        setAdminKey(password);
        localStorage.setItem('admin_authenticated', 'true');
        localStorage.setItem('admin_key', password);
        toast.success("Admin access granted");
        fetchData(password);
        fetchAppSettings(password);
        fetchSalesStats(password);
        fetchLmsCredits();
        setTimeout(() => fetchSupportTickets("open", password), 600);
      }
    } catch (error) {
      toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (key) => {
    try {
      // Students NOT loaded on init — search-only to avoid 1800+ user query
      const [statsRes, emailStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?admin_key=${key}`),
        axios.get(`${API}/admin/email-stats?admin_key=${key}`)
      ]);
      setStudents([]);  // empty until searched
      setStats(statsRes.data);
      setEmailStats(emailStatsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  };

  // Debounced server-side student search
  useEffect(() => {
    if (!adminKey) return;
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2 && !trimmed) {
      setStudents([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/admin/students?admin_key=${adminKey}&search=${encodeURIComponent(trimmed)}`);
        setStudents(res.data.students || []);
      } catch {}
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, adminKey]);

  const exportToCSV = () => {
    const headers = ["Name", "Mobile", "Email", "Plan", "Questions Used", "Questions Limit", "MCQ Attempts", "Credits Used", "Active Today", "Last Active", "Registered Date"];
    const rows = students.map(s => [
      s.name || "",
      s.mobile || "",
      s.email || "",
      s.plan || "free",
      s.questions_used || 0,
      s.questions_limit || 1,
      s.mcq_attempts || 0,
      s.estimated_credits || 0,
      s.is_active_today ? "Yes" : "No",
      s.last_active ? format(new Date(s.last_active), "yyyy-MM-dd HH:mm") : "Never",
      s.created_at ? format(new Date(s.created_at), "yyyy-MM-dd") : ""
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    toast.success("CSV exported successfully");
  };

  const handleCsvImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('admin_key', adminKey);
      formData.append('file', csvFile);
      formData.append('send_emails', sendEmails);

      const response = await axios.post(`${API}/admin/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      const emailMsg = sendEmails && response.data.results.emails_sent > 0 
        ? `, ${response.data.results.emails_sent} emails sent` 
        : '';
      toast.success(`Import complete! ${response.data.results.new_users} new users, ${response.data.results.upgraded_users} upgraded${emailMsg}`);
      
      // Refresh data
      fetchData(adminKey);
      setCsvFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    switch(plan) {
      case 'unlimited':
        return <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white"><Crown className="w-3 h-3 mr-1" />Unlimited</Badge>;
      case 'premium':
      case 'tier2_pro':
      case 'lokesh_notes':
      case 'standard':
      case 'basic':
        return <Badge className="bg-gradient-to-r from-green-400 to-emerald-500 text-white"><Crown className="w-3 h-3 mr-1" />Premium</Badge>;
      default:
        return <Badge variant="secondary">Free</Badge>;
    }
  };

  const getActivityBadge = (isActive) => {
    if (isActive) {
      return <Badge className="bg-green-500 text-white animate-pulse"><Activity className="w-3 h-3 mr-1" />Active</Badge>;
    }
    return <Badge variant="outline" className="border-gray-500 text-gray-400">Offline</Badge>;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Access</CardTitle>
            <CardDescription>Enter admin password to view students data</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Verifying..." : "Access Dashboard"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-blue-600">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <nav className="bg-black/30 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-purple-400" />
              <span className="text-xl font-bold text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={exportToCSV} className="border-white/20 text-white hover:bg-white/10">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  Back to App
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.total_users}</p>
                    <p className="text-sm text-gray-400">Total Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/20 rounded-xl">
                    <Activity className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.active_users_today || 0}</p>
                    <p className="text-sm text-gray-400">Active Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <FileText className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.total_submissions}</p>
                    <p className="text-sm text-gray-400">Descriptive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-500/20 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.total_mcq_attempts || 0}</p>
                    <p className="text-sm text-gray-400">MCQ Attempts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credits & Cache Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-yellow-900/50 to-orange-900/50 border-yellow-500/30 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-500/20 rounded-xl">
                    <CreditCard className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.estimated_total_credits || 0}</p>
                    <p className="text-sm text-gray-400">Total Credits Used</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-cyan-900/50 to-blue-900/50 border-cyan-500/30 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-cyan-500/20 rounded-xl">
                    <Database className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.cached_questions || 0}</p>
                    <p className="text-sm text-gray-400">Cached Questions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-pink-900/50 to-rose-900/50 border-pink-500/30 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-pink-500/20 rounded-xl">
                    <Database className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.cached_mcq_sets || 0}</p>
                    <p className="text-sm text-gray-400">Cached MCQ Sets</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-900/50 to-green-900/50 border-emerald-500/30 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{stats.recent_submissions_7d}</p>
                    <p className="text-sm text-gray-400">Last 7 Days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Breakdown */}
        {stats && (
          <Card className="bg-white/10 border-white/20 text-white mb-8">
            <CardHeader>
              <CardTitle>Plan Distribution</CardTitle>
            </CardHeader>
            <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-7 gap-4 text-center">
                <div className="p-4 bg-gray-500/20 rounded-xl">
                  <p className="text-2xl font-bold">{stats.plans.free || 0}</p>
                  <p className="text-sm text-gray-400">Free</p>
                </div>
                <div className="p-4 bg-green-500/20 rounded-xl">
                  <p className="text-2xl font-bold">{(stats.plans.premium || 0) + (stats.plans.lokesh_notes || 0) + (stats.plans.tier2_pro || 0) + (stats.plans.basic || 0) + (stats.plans.standard || 0)}</p>
                  <p className="text-sm text-gray-400">Premium</p>
                </div>
                <div className="p-4 bg-yellow-500/20 rounded-xl">
                  <p className="text-2xl font-bold">{stats.plans.unlimited || 0}</p>
                  <p className="text-sm text-gray-400">Unlimited</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ⚙️ APP SETTINGS */}
        <Card className="bg-gradient-to-br from-violet-900/60 to-purple-900/60 border-violet-500/40 text-white mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-violet-300">
              <Database className="w-6 h-6" />
              Question Limits Settings
            </CardTitle>
            <CardDescription className="text-gray-300">
              Configure question limits for free and paid users
            </CardDescription>
          </CardHeader>
          <CardContent>
            {settingsLoading ? (
              <div className="text-center py-8 text-gray-400">Loading settings...</div>
            ) : appSettings ? (
              <div className="space-y-6">
                {/* Running Header / Marquee */}
                <div className="border border-yellow-500/30 rounded-lg p-4 bg-yellow-900/10">
                  <h3 className="text-lg font-semibold text-yellow-300 mb-3 flex items-center gap-2">
                    <Megaphone className="w-5 h-5" />
                    Running Header (Marquee)
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-400">Enable</label>
                      <button
                        onClick={() => setAppSettings({...appSettings, running_header_enabled: !appSettings.running_header_enabled})}
                        className={`w-12 h-6 rounded-full transition-colors ${appSettings.running_header_enabled ? 'bg-yellow-500' : 'bg-gray-600'} relative`}
                        data-testid="toggle-running-header"
                      >
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${appSettings.running_header_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                      <span className="text-xs text-gray-500">{appSettings.running_header_enabled ? "ON — visible to students" : "OFF"}</span>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Scrolling Text</label>
                      <textarea
                        value={appSettings.running_header_text || ""}
                        onChange={(e) => setAppSettings({...appSettings, running_header_text: e.target.value})}
                        className="w-full bg-black/30 border border-yellow-500/50 text-white rounded-md px-3 py-2 text-sm min-h-[60px]"
                        placeholder="Enter announcement text that will scroll right to left..."
                        data-testid="running-header-text-input"
                      />
                    </div>
                    {appSettings.running_header_enabled && appSettings.running_header_text && (
                      <div className="border border-yellow-500 rounded overflow-hidden bg-yellow-50">
                        <div className="py-1 overflow-hidden">
                          <p className="text-xs font-bold text-red-700 uppercase tracking-wide whitespace-nowrap animate-pulse">
                            Preview: {appSettings.running_header_text}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Descriptive Question Limits */}
                <div>
                  <h3 className="text-lg font-semibold text-violet-300 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Descriptive Question Limits
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Free Users</label>
                      <Input
                        type="number"
                        value={appSettings.free_descriptive_limit}
                        onChange={(e) => setAppSettings({...appSettings, free_descriptive_limit: parseInt(e.target.value) || 0})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Paid Users</label>
                      <Input
                        type="number"
                        value={appSettings.paid_descriptive_limit}
                        onChange={(e) => setAppSettings({...appSettings, paid_descriptive_limit: parseInt(e.target.value) || 0})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Unlimited Plan (-1)</label>
                      <Input
                        type="number"
                        value={appSettings.unlimited_descriptive_limit}
                        onChange={(e) => setAppSettings({...appSettings, unlimited_descriptive_limit: parseInt(e.target.value)})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* MCQ Limits */}
                <div>
                  <h3 className="text-lg font-semibold text-violet-300 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    MCQ Per Topic Limits
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Free Users</label>
                      <Input
                        type="number"
                        value={appSettings.free_mcq_per_topic}
                        onChange={(e) => setAppSettings({...appSettings, free_mcq_per_topic: parseInt(e.target.value) || 0})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Paid Users</label>
                      <Input
                        type="number"
                        value={appSettings.paid_mcq_per_topic}
                        onChange={(e) => setAppSettings({...appSettings, paid_mcq_per_topic: parseInt(e.target.value) || 0})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Unlimited Plan</label>
                      <Input
                        type="number"
                        value={appSettings.unlimited_mcq_per_topic}
                        onChange={(e) => setAppSettings({...appSettings, unlimited_mcq_per_topic: parseInt(e.target.value) || 0})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Other Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-violet-300 mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Other Settings
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">MCQ Batch Size</label>
                      <Input
                        type="number"
                        value={appSettings.mcq_batch_size}
                        onChange={(e) => setAppSettings({...appSettings, mcq_batch_size: parseInt(e.target.value) || 10})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">PDF Retention (Days)</label>
                      <Input
                        type="number"
                        value={appSettings.pdf_retention_days}
                        onChange={(e) => setAppSettings({...appSettings, pdf_retention_days: parseInt(e.target.value) || 2})}
                        className="bg-black/30 border-violet-500/50 text-white"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={appSettings.include_graphs_for_economics}
                          onChange={(e) => setAppSettings({...appSettings, include_graphs_for_economics: e.target.checked})}
                          className="w-4 h-4 accent-violet-500"
                        />
                        <span className="text-sm text-gray-300">Economics Graphs</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={updateAppSettings}
                    disabled={savingSettings}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    {savingSettings ? "Saving..." : "Save Settings"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">Failed to load settings</div>
            )}
          </CardContent>
        </Card>

        {/* 💰 SALES DASHBOARD */}
        <Card className="bg-gradient-to-br from-emerald-900/60 to-teal-900/60 border-emerald-500/40 text-white mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-300">
              <IndianRupee className="w-6 h-6" />
              Sales Dashboard (Razorpay)
            </CardTitle>
            <CardDescription className="text-gray-300">
              Real-time payment analytics from Razorpay
            </CardDescription>
          </CardHeader>
          <CardContent>
            {salesLoading ? (
              <div className="text-center py-8 text-gray-400">Loading sales data...</div>
            ) : salesStats ? (
              <>
                {/* Sales Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {/* Today */}
                  <div className="p-4 bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-xl border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingCart className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-300 font-medium">Today</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{salesStats.today?.count || 0}</p>
                    <p className="text-lg text-green-400 flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {salesStats.today?.revenue || 0}
                    </p>
                  </div>
                  
                  {/* Yesterday */}
                  <div className="p-4 bg-gradient-to-br from-blue-600/30 to-cyan-600/30 rounded-xl border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-blue-300 font-medium">Yesterday</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{salesStats.yesterday?.count || 0}</p>
                    <p className="text-lg text-blue-400 flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {salesStats.yesterday?.revenue || 0}
                    </p>
                  </div>
                  
                  {/* This Month */}
                  <div className="p-4 bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarDays className="w-5 h-5 text-purple-400" />
                      <span className="text-sm text-purple-300 font-medium">This Month</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{salesStats.this_month?.count || 0}</p>
                    <p className="text-lg text-purple-400 flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {salesStats.this_month?.revenue || 0}
                    </p>
                  </div>
                  
                  {/* All Time */}
                  <div className="p-4 bg-gradient-to-br from-yellow-600/30 to-orange-600/30 rounded-xl border border-yellow-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-yellow-400" />
                      <span className="text-sm text-yellow-300 font-medium">All Time</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{salesStats.all_time?.count || 0}</p>
                    <p className="text-lg text-yellow-400 flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {salesStats.all_time?.revenue || 0}
                    </p>
                  </div>
                </div>

                {/* Calendar View */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCalendarMonthChange(-1)}
                        className="text-white hover:bg-white/10"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <span className="text-lg font-semibold text-white">
                        {new Date(calendarYear, calendarMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCalendarMonthChange(1)}
                        className="text-white hover:bg-white/10"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs text-gray-400 font-medium py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                      {renderCalendar()}
                    </div>
                    
                    {/* Month Total */}
                    {calendarData && (
                      <div className="mt-4 p-3 bg-emerald-600/20 rounded-lg border border-emerald-500/30">
                        <div className="flex justify-between items-center">
                          <span className="text-emerald-300 font-medium">Month Total:</span>
                          <div className="text-right">
                            <span className="text-white font-bold">{calendarData.month_total?.count || 0} sales</span>
                            <span className="text-emerald-400 ml-3 flex items-center gap-1 inline-flex">
                              <IndianRupee className="w-4 h-4" />
                              {calendarData.month_total?.revenue || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Date Details / Recent Transactions */}
                  <div className="bg-black/30 rounded-xl p-4">
                    {selectedDate && selectedDateSales ? (
                      <>
                        <h4 className="text-lg font-semibold text-white mb-3">
                          Sales on {format(new Date(selectedDate), 'MMMM d, yyyy')}
                        </h4>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="p-3 bg-green-600/20 rounded-lg text-center">
                            <p className="text-2xl font-bold text-white">{selectedDateSales.total_sales}</p>
                            <p className="text-sm text-gray-400">Sales</p>
                          </div>
                          <div className="p-3 bg-yellow-600/20 rounded-lg text-center">
                            <p className="text-2xl font-bold text-yellow-400 flex items-center justify-center gap-1">
                              <IndianRupee className="w-5 h-5" />
                              {selectedDateSales.total_revenue}
                            </p>
                            <p className="text-sm text-gray-400">Revenue</p>
                          </div>
                        </div>
                        
                        {/* Transactions for selected date */}
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {selectedDateSales.transactions?.map((tx, idx) => (
                            <div key={idx} className="p-2 bg-white/5 rounded-lg flex justify-between items-center text-sm">
                              <div>
                                <p className="text-white font-medium">{tx.user_name || 'User'}</p>
                                <p className="text-gray-400 text-xs">{tx.plan_name}</p>
                              </div>
                              <span className="text-green-400 font-semibold">₹{tx.amount_inr}</span>
                            </div>
                          ))}
                          {(!selectedDateSales.transactions || selectedDateSales.transactions.length === 0) && (
                            <p className="text-center text-gray-400 py-4">No transactions on this date</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <h4 className="text-lg font-semibold text-white mb-3">Recent Transactions</h4>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {salesStats.recent_transactions?.map((tx, idx) => (
                            <div key={idx} className="p-3 bg-white/5 rounded-lg flex justify-between items-center">
                              <div>
                                <p className="text-white font-medium">{tx.user_name || 'User'}</p>
                                <p className="text-gray-400 text-xs">{tx.plan_name}</p>
                                <p className="text-gray-500 text-xs">
                                  {tx.created_at ? formatDistanceToNow(new Date(tx.created_at), { addSuffix: true }) : ''}
                                </p>
                              </div>
                              <span className="text-green-400 font-bold text-lg">₹{tx.amount_inr}</span>
                            </div>
                          ))}
                          {(!salesStats.recent_transactions || salesStats.recent_transactions.length === 0) && (
                            <p className="text-center text-gray-400 py-8">No transactions yet</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <IndianRupee className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No sales data available</p>
                <p className="text-sm mt-2">Sales will appear here after first Razorpay payment</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* LMS Credits Usage */}
        {lmsCredits && (
          <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30 text-white mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-400" />
                LMS Credits Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-purple-500/20 rounded-xl">
                  <p className="text-2xl font-bold text-purple-300">${lmsCredits.estimated_total_credits?.toFixed(2)}</p>
                  <p className="text-sm text-gray-400">Total Credits Used</p>
                </div>
                <div className="p-4 bg-pink-500/20 rounded-xl">
                  <p className="text-2xl font-bold text-pink-300">{lmsCredits.total_notes}</p>
                  <p className="text-sm text-gray-400">Notes Created</p>
                </div>
                <div className="p-4 bg-blue-500/20 rounded-xl">
                  <p className="text-2xl font-bold text-blue-300">{lmsCredits.notes_with_images}</p>
                  <p className="text-sm text-gray-400">With Images</p>
                </div>
                <div className="p-4 bg-green-500/20 rounded-xl">
                  <p className="text-2xl font-bold text-green-300">{lmsCredits.completed_bulk_jobs}</p>
                  <p className="text-sm text-gray-400">Bulk Jobs Done</p>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Link to="/lms">
                  <Button variant="outline" size="sm" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20">
                    Open LMS Manager →
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* CSV Import Section - Premium Plan */}
        <Card className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-500/30 text-white mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-400" />
              Import Students — Premium Plan
            </CardTitle>
            <CardDescription className="text-gray-300">
              Upload CSV file to grant Premium Plan access to your batch students (300 questions)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCsvImport} className="space-y-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm text-gray-400 mb-2">Select CSV File</label>
                  <Input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="bg-white/10 border-white/20 text-white file:bg-green-600 file:text-white file:border-0 file:rounded file:mr-4"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={importLoading || !csvFile}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importLoading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import & Grant Access
                    </>
                  )}
                </Button>
              </div>

              {/* Email Toggle */}
              <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="send-emails"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                  className="w-5 h-5 rounded border-blue-500 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="send-emails" className="flex items-center gap-2 text-blue-300 cursor-pointer">
                  <Send className="w-4 h-4" />
                  <span>Send welcome email with login credentials to new users</span>
                </label>
              </div>

              <div className="p-3 bg-black/20 rounded-lg text-sm">
                <p className="text-gray-300 mb-2">CSV Format Required:</p>
                <p className="text-gray-400">Columns: Name, Email, Phone No.</p>
                <p className="text-gray-400 mt-1">New users get Premium Plan (300 questions, unlimited MCQs, full notes)</p>
                <p className="text-gray-400">Existing free/lokesh_notes users will be upgraded to Premium</p>
                <p className="text-gray-400">Default password: DT@ + last 4 digits of mobile</p>
                {sendEmails && <p className="text-blue-400 mt-1">Welcome email will be sent to users with valid email addresses</p>}
              </div>
            </form>

            {/* Import Results */}
            {importResult && (
              <div className="mt-4 p-4 bg-black/30 rounded-lg">
                <h4 className="font-semibold text-green-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Import Results
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <p className="text-2xl font-bold">{importResult.results.total}</p>
                    <p className="text-xs text-gray-400">Total Rows</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-400">{importResult.results.new_users}</p>
                    <p className="text-xs text-gray-400">New Users</p>
                  </div>
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-400">{importResult.results.upgraded_users}</p>
                    <p className="text-xs text-gray-400">Upgraded</p>
                  </div>
                  <div className="p-3 bg-gray-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-gray-400">{importResult.results.skipped}</p>
                    <p className="text-xs text-gray-400">Skipped</p>
                  </div>
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-purple-400">{importResult.results.emails_sent || 0}</p>
                    <p className="text-xs text-gray-400">Emails Sent</p>
                  </div>
                  <div className="p-3 bg-orange-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-orange-400">{importResult.results.emails_failed || 0}</p>
                    <p className="text-xs text-gray-400">Email Failed</p>
                  </div>
                </div>
                {importResult.results.errors.length > 0 && (
                  <div className="mt-3 p-2 bg-red-500/10 rounded text-sm">
                    <p className="text-red-400 flex items-center gap-1 mb-1">
                      <AlertCircle className="w-4 h-4" />
                      Errors ({importResult.results.errors.length}):
                    </p>
                    <ul className="text-gray-400 text-xs max-h-20 overflow-y-auto">
                      {importResult.results.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                      {importResult.results.errors.length > 5 && (
                        <li>...and {importResult.results.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Email Section */}
        <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-400" />
              Send Welcome Email to Students
            </CardTitle>
            <CardDescription className="text-gray-300">
              Send login credentials to students with valid email addresses
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Email Stats */}
            {emailStats && (
              <div>
                <div className="grid grid-cols-4 gap-3 text-center mb-3">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <p className="text-2xl font-bold">{emailStats.total_users}</p>
                    <p className="text-xs text-gray-400">Total Users</p>
                  </div>
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-400">{emailStats.users_with_email}</p>
                    <p className="text-xs text-gray-400">With Email</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-400">{emailStats.emails_sent}</p>
                    <p className="text-xs text-gray-400">Emails Sent ✓</p>
                  </div>
                  <div className="p-3 bg-orange-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-orange-400">{emailStats.emails_remaining}</p>
                    <p className="text-xs text-gray-400">Remaining</p>
                  </div>
                </div>
                {/* Explanation row */}
                {(emailStats.google_auth_users_skipped > 0 || emailStats.permanently_failed > 0) && (
                  <div className="flex gap-3 mb-4 text-xs">
                    {emailStats.google_auth_users_skipped > 0 && (
                      <div className="flex items-center gap-1 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-1 text-blue-300">
                        <span>🔵 {emailStats.google_auth_users_skipped} Google Auth users (no password needed — will be cleared on next send)</span>
                      </div>
                    )}
                    {emailStats.permanently_failed > 0 && (
                      <div className="flex items-center gap-1 bg-red-500/10 border border-red-500/20 rounded px-2 py-1 text-red-300">
                        <span>🔴 {emailStats.permanently_failed} invalid/bounced emails</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              {/* Send to All Button */}
              <Button
                onClick={handleBulkEmail}
                disabled={bulkEmailLoading || remainingEmailLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {bulkEmailLoading ? (
                  <>
                    <Send className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to ALL Students
                  </>
                )}
              </Button>

              {/* Send to Remaining Button */}
              <Button
                onClick={handleRemainingEmail}
                disabled={remainingEmailLoading || bulkEmailLoading || (emailStats?.emails_remaining === 0)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {remainingEmailLoading ? (
                  <>
                    <Send className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to Remaining ({emailStats?.emails_remaining || 0})
                  </>
                )}
              </Button>
            </div>

            <p className="text-gray-400 text-sm mt-3">
              ⚠️ "Send to ALL" sends to everyone. "Send to Remaining" only sends to students who haven't received email yet.
              <br />Worker processes emails in background — no timeout, live progress below.

              {/* Live email job progress */}
              {emailJobStatus && (
                <div data-testid="email-job-progress" className={`mt-4 p-4 rounded-lg border ${
                  emailJobStatus.status === 'completed' ? 'border-green-500 bg-green-900/20' :
                  emailJobStatus.status === 'failed' ? 'border-red-500 bg-red-900/20' :
                  'border-blue-500 bg-blue-900/20'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">Email Job Progress</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      emailJobStatus.status === 'completed' ? 'bg-green-600' :
                      emailJobStatus.status === 'failed' ? 'bg-red-600' :
                      'bg-blue-600 animate-pulse'
                    }`}>{emailJobStatus.status?.toUpperCase()}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    <div><p className="text-gray-400">Total</p><p className="text-white font-bold">{emailJobStatus.total || '—'}</p></div>
                    <div><p className="text-gray-400">Sent</p><p className="text-green-400 font-bold">{emailJobStatus.sent || 0}</p></div>
                    <div><p className="text-gray-400">Failed</p><p className="text-red-400 font-bold">{emailJobStatus.failed || 0}</p></div>
                    <div><p className="text-gray-400">Processed</p><p className="text-blue-400 font-bold">{emailJobStatus.processed || 0}</p></div>
                  </div>
                  {emailJobStatus.status === 'processing' && emailJobStatus.total > 0 && (
                    <div className="mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all duration-500" style={{width: `${Math.round(((emailJobStatus.processed || 0) / emailJobStatus.total) * 100)}%`}} />
                    </div>
                  )}
                </div>
              )}
            </p>

            {/* Fix Passwords Button */}
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-400 font-medium">⚠️ Password Issue Detected</p>
                  <p className="text-gray-400 text-sm">Some users may have missing passwords. Fix them before sending emails.</p>
                </div>
                <Button
                  onClick={handleFixPasswords}
                  disabled={fixingPasswords}
                  variant="outline"
                  className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20"
                >
                  {fixingPasswords ? "Fixing..." : "Fix All Passwords"}
                </Button>
              </div>
            </div>

            {/* Bulk Email Results */}
            {bulkEmailResult && (
              <div className="mt-4 p-4 bg-black/30 rounded-lg">
                <h4 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Bulk Email Results
                </h4>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <p className="text-2xl font-bold">{bulkEmailResult.total_users}</p>
                    <p className="text-xs text-gray-400">Total Users</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-400">{bulkEmailResult.emails_sent}</p>
                    <p className="text-xs text-gray-400">Emails Sent</p>
                  </div>
                  <div className="p-3 bg-red-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-red-400">{bulkEmailResult.emails_failed}</p>
                    <p className="text-xs text-gray-400">Failed</p>
                  </div>
                  <div className="p-3 bg-gray-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-gray-400">{bulkEmailResult.skipped_no_email}</p>
                    <p className="text-xs text-gray-400">No Email</p>
                  </div>
                </div>
              </div>
            )}

            {/* Remaining Email Results */}
            {remainingEmailResult && (
              <div className="mt-4 p-4 bg-black/30 rounded-lg">
                <h4 className="font-semibold text-orange-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Remaining Students Email Results
                </h4>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <p className="text-2xl font-bold">{remainingEmailResult.total_remaining}</p>
                    <p className="text-xs text-gray-400">Total Remaining</p>
                  </div>
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-400">{remainingEmailResult.emails_sent}</p>
                    <p className="text-xs text-gray-400">Emails Sent</p>
                  </div>
                  <div className="p-3 bg-red-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-red-400">{remainingEmailResult.emails_failed}</p>
                    <p className="text-xs text-gray-400">Failed</p>
                  </div>
                  <div className="p-3 bg-gray-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-gray-400">{remainingEmailResult.skipped_no_email}</p>
                    <p className="text-xs text-gray-400">No Email</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Support Tickets ──────────────────────────────────────── */}
        <Card className="bg-gradient-to-br from-slate-900/80 to-gray-900/80 border-slate-600/40 text-white mb-8">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-slate-300">
                  <Ticket className="w-5 h-5" />
                  Support Tickets
                  {supportTickets.filter(t => t.status === "open" || t.status === "replied").length > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold ml-1">
                      {supportTickets.filter(t => t.status === "open" || t.status === "replied").length} open
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 mt-1">
                  Student support requests submitted via Help bot
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* Filter tabs */}
                {["open", "resolved", "all"].map(f => (
                  <button
                    key={f}
                    onClick={() => { setTicketsFilter(f); fetchSupportTickets(f); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${ticketsFilter === f ? 'bg-slate-500 text-white' : 'bg-slate-800 text-gray-400 hover:text-white'}`}
                    data-testid={`tickets-filter-${f}`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchSupportTickets(ticketsFilter)}
                  disabled={ticketsLoading}
                  className="border-slate-600/50 text-slate-300 hover:bg-slate-700/30 text-xs"
                  data-testid="tickets-refresh-btn"
                >
                  {ticketsLoading ? "Loading..." : "Refresh"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ticketsLoading ? (
              <div className="text-center py-8 text-gray-400 text-sm">Loading tickets...</div>
            ) : supportTickets.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <InboxIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No {ticketsFilter === "all" ? "" : ticketsFilter} tickets yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                {supportTickets.map((ticket, i) => (
                  <div
                    key={ticket.ticket_id || i}
                    className={`rounded-xl border p-4 transition-colors ${
                      ticket.status === "resolved" ? 'border-green-600/20 bg-green-900/10' :
                      ticket.status === "replied"  ? 'border-blue-500/30 bg-blue-900/10' :
                      'border-amber-500/30 bg-amber-900/10'
                    }`}
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Name + Status */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white text-sm">{ticket.name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            ticket.status === 'resolved' ? 'bg-green-700/40 text-green-400' :
                            ticket.status === 'replied'  ? 'bg-blue-700/40 text-blue-400' :
                            'bg-amber-700/40 text-amber-400'
                          }`}>
                            {ticket.status.toUpperCase()}
                          </span>
                        </div>
                        {/* Contact info */}
                        <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-2">
                          {ticket.phone && (
                            <span className="flex items-center gap-1">
                              <span>📞</span> {ticket.phone}
                            </span>
                          )}
                          {ticket.email && (
                            <span className="flex items-center gap-1 truncate">
                              <span>✉️</span> {ticket.email}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ticket.created_at ? new Date(ticket.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                          </span>
                        </div>
                        {/* Issue message */}
                        <div className="bg-black/20 rounded-lg px-3 py-2 text-sm text-gray-200 leading-relaxed">
                          {ticket.message}
                        </div>
                        {/* Admin reply (if sent) */}
                        {ticket.admin_reply && (
                          <div className="mt-2 bg-blue-900/20 border border-blue-500/30 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-blue-400 font-bold mb-1 uppercase tracking-wide">Your Reply (Student Sees This)</p>
                            <p className="text-sm text-blue-200">{ticket.admin_reply}</p>
                            {ticket.admin_reply_at && (
                              <p className="text-[10px] text-blue-400/60 mt-1">
                                {new Date(ticket.admin_reply_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                                {ticket.reply_seen ? " · Seen by student" : " · Not yet seen"}
                              </p>
                            )}
                          </div>
                        )}
                        {ticket.admin_notes && (
                          <p className="text-xs text-green-400 mt-1.5 italic">Note: {ticket.admin_notes}</p>
                        )}

                        {/* Reply box (inline) */}
                        {replyingTicket === ticket.ticket_id && (
                          <div className="mt-3 space-y-2">
                            <textarea
                              value={replyTexts[ticket.ticket_id] || ""}
                              onChange={e => setReplyTexts(prev => ({ ...prev, [ticket.ticket_id]: e.target.value }))}
                              placeholder="Type your reply to the student..."
                              rows={3}
                              className="w-full bg-slate-800 border border-blue-500/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-400 resize-none"
                              data-testid={`ticket-reply-input-${ticket.ticket_id}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => sendReply(ticket.ticket_id)}
                                disabled={sendingReply === ticket.ticket_id || !(replyTexts[ticket.ticket_id] || "").trim()}
                                className="bg-blue-600 hover:bg-blue-500 text-white text-xs"
                                data-testid={`ticket-reply-send-${ticket.ticket_id}`}
                              >
                                {sendingReply === ticket.ticket_id ? "Sending..." : "Send Reply"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setReplyingTicket(null)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700/30 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {ticket.status !== "resolved" && (
                          <Button
                            size="sm"
                            onClick={() => setReplyingTicket(replyingTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                            className="bg-blue-700 hover:bg-blue-600 text-white text-xs"
                            data-testid={`ticket-reply-btn-${ticket.ticket_id}`}
                          >
                            Reply
                          </Button>
                        )}
                        {ticket.status === "open" && (
                          <Button
                            size="sm"
                            onClick={() => resolveTicket(ticket.ticket_id)}
                            disabled={resolvingTicket === ticket.ticket_id}
                            className="bg-green-700 hover:bg-green-600 text-white text-xs shrink-0"
                            data-testid={`ticket-resolve-${ticket.ticket_id}`}
                          >
                            <CheckCheck className="w-3.5 h-3.5 mr-1" />
                            {resolvingTicket === ticket.ticket_id ? "..." : "Resolve"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Email Broadcast ─────────────────────────────────────── */}
        <Card className="bg-gradient-to-br from-indigo-900/60 to-blue-900/60 border-indigo-500/40 text-white mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-indigo-300">
                  <Radio className="w-6 h-6" />
                  Email Broadcast
                </CardTitle>
                <CardDescription className="text-gray-300 mt-1">
                  Send custom emails to enrolled students, non-enrolled users, or everyone
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/20"
                onClick={() => { setShowBroadcastHistory(h => !h); if (!showBroadcastHistory) fetchBroadcastHistory(); }}
                data-testid="broadcast-history-toggle"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">

            {/* Broadcast History Panel */}
            {showBroadcastHistory && (
              <div className="bg-black/30 rounded-xl border border-indigo-500/20 p-4">
                <h4 className="text-sm font-semibold text-indigo-300 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" /> Past Broadcasts
                </h4>
                {broadcastHistoryLoading ? (
                  <p className="text-gray-400 text-sm">Loading...</p>
                ) : broadcastHistory.length === 0 ? (
                  <p className="text-gray-500 text-sm">No broadcasts sent yet.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {broadcastHistory.map((b, i) => (
                      <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{b.subject}</p>
                          <p className="text-gray-400 text-xs">
                            {b.segment} · {b.queued_at ? format(new Date(b.queued_at), "MMM d, yyyy HH:mm") : "—"}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 ml-3 text-xs shrink-0">
                          <span className="text-green-400">{b.sent || 0} sent</span>
                          {b.failed > 0 && <span className="text-red-400">{b.failed} failed</span>}
                          <span className={`px-2 py-0.5 rounded-full font-medium ${
                            b.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            b.status === 'processing' ? 'bg-blue-500/20 text-blue-400 animate-pulse' :
                            b.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>{b.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Row 1: Subject */}
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Email Subject *</label>
              <Input
                value={broadcastSubject}
                onChange={e => setBroadcastSubject(e.target.value)}
                placeholder="e.g. Important Update from Descriptive Test"
                className="bg-black/30 border-indigo-500/40 text-white placeholder-gray-500"
                data-testid="broadcast-subject-input"
              />
            </div>

            {/* Row 2: Rich Text Editor */}
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">
                Email Body *
                {broadcastPersonalize && (
                  <span className="ml-2 text-xs text-indigo-400 font-normal">
                    — Use <code className="bg-indigo-900/50 px-1 rounded">{"{{name}}"}</code> to insert student's name
                  </span>
                )}
              </label>
              <div className="rounded-lg overflow-hidden border border-indigo-500/40">
                {/* Toolbar */}
                <div className="flex items-center gap-1 bg-slate-800 border-b border-indigo-500/30 px-2 py-1.5 flex-wrap">
                  {[
                    { cmd: 'bold',          label: 'B', title: 'Bold',         style: 'font-bold' },
                    { cmd: 'italic',        label: 'I', title: 'Italic',       style: 'italic' },
                    { cmd: 'underline',     label: 'U', title: 'Underline',    style: 'underline' },
                    { cmd: 'insertUnorderedList', label: '• List', title: 'Bullet List', style: '' },
                    { cmd: 'insertOrderedList',   label: '1. List', title: 'Numbered List', style: '' },
                  ].map(({ cmd, label, title, style }) => (
                    <button
                      key={cmd}
                      type="button"
                      title={title}
                      onMouseDown={e => { e.preventDefault(); document.execCommand(cmd, false, null); editorRef.current?.focus(); }}
                      className={`px-2 py-0.5 rounded text-xs text-gray-300 hover:bg-indigo-600 hover:text-white transition-colors ${style}`}
                    >{label}</button>
                  ))}
                  <button
                    type="button"
                    title="Insert Link"
                    onMouseDown={e => {
                      e.preventDefault();
                      const url = window.prompt("Enter URL:");
                      if (url) document.execCommand('createLink', false, url);
                      editorRef.current?.focus();
                    }}
                    className="px-2 py-0.5 rounded text-xs text-gray-300 hover:bg-indigo-600 hover:text-white transition-colors"
                  >Link</button>
                  <button
                    type="button"
                    title="Clear formatting"
                    onMouseDown={e => { e.preventDefault(); document.execCommand('removeFormat', false, null); editorRef.current?.focus(); }}
                    className="px-2 py-0.5 rounded text-xs text-gray-400 hover:bg-red-600/50 hover:text-white transition-colors ml-auto"
                  >Clear</button>
                </div>
                {/* Editable area */}
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={e => setBroadcastBody(e.currentTarget.innerHTML)}
                  className="min-h-[180px] px-3 py-2 text-sm text-gray-900 bg-white outline-none"
                  style={{ lineHeight: "1.6" }}
                  data-placeholder="Write your email content here…"
                  data-testid="broadcast-body-editor"
                />
              </div>
            </div>

            {/* Row 3: Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Audience Segment */}
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Target Audience</label>
                <select
                  value={broadcastSegment}
                  onChange={e => { setBroadcastSegment(e.target.value); setBroadcastPreviewCount(null); }}
                  className="w-full bg-black/30 border border-indigo-500/40 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  data-testid="broadcast-segment-select"
                >
                  <option value="both">All Users (Enrolled + Not Enrolled)</option>
                  <option value="enrolled">Enrolled (Paid Students Only)</option>
                  <option value="not_enrolled">Not Enrolled (Free Users)</option>
                </select>
              </div>

              {/* Personalization Toggle */}
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Personalization</label>
                <button
                  onClick={() => setBroadcastPersonalize(p => !p)}
                  className={`flex items-center gap-3 w-full px-3 py-2 rounded-md border text-sm transition-colors ${
                    broadcastPersonalize
                      ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                      : 'border-gray-600 bg-black/20 text-gray-400'
                  }`}
                  data-testid="broadcast-personalize-toggle"
                >
                  <span className={`w-8 h-4 rounded-full relative flex-shrink-0 transition-colors ${broadcastPersonalize ? 'bg-indigo-500' : 'bg-gray-600'}`}>
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${broadcastPersonalize ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </span>
                  {broadcastPersonalize ? "Hi {{name}}, ..." : "No personalization"}
                </button>
              </div>

              {/* Schedule */}
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">
                  Schedule (optional)
                  <span className="text-xs text-gray-500 ml-1">— leave blank to send now</span>
                </label>
                <Input
                  type="datetime-local"
                  value={broadcastSchedule}
                  onChange={e => setBroadcastSchedule(e.target.value)}
                  className="bg-black/30 border-indigo-500/40 text-white [color-scheme:dark]"
                  data-testid="broadcast-schedule-input"
                />
              </div>
            </div>

            {/* Row 4: Preview + Send */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button
                variant="outline"
                onClick={fetchBroadcastPreview}
                disabled={broadcastPreviewLoading}
                className="border-indigo-400 text-indigo-300 hover:bg-indigo-500/20"
                data-testid="broadcast-preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                {broadcastPreviewLoading ? "Counting..." : "Preview Recipients"}
              </Button>

              {broadcastPreviewCount !== null && (
                <span className="text-sm font-semibold text-indigo-200 bg-indigo-900/40 border border-indigo-500/30 px-3 py-1.5 rounded-lg" data-testid="broadcast-preview-count">
                  {broadcastPreviewCount} recipient{broadcastPreviewCount !== 1 ? 's' : ''} will receive this email
                </span>
              )}

              <Button
                onClick={handleSendBroadcast}
                disabled={broadcastSending || !broadcastSubject.trim()}
                className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="broadcast-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {broadcastSending ? "Queuing..." : broadcastSchedule ? "Schedule Broadcast" : "Send Broadcast Now"}
              </Button>
            </div>

            {/* Live Progress */}
            {broadcastJobStatus && (
              <div className={`rounded-lg border p-4 ${
                broadcastJobStatus.status === 'completed' ? 'border-green-500 bg-green-900/20' :
                broadcastJobStatus.status === 'failed' ? 'border-red-500 bg-red-900/20' :
                'border-indigo-500 bg-indigo-900/20'
              }`} data-testid="broadcast-job-progress">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-white flex items-center gap-2">
                    <Radio className="w-4 h-4" /> Broadcast Progress
                  </span>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                    broadcastJobStatus.status === 'completed' ? 'bg-green-600' :
                    broadcastJobStatus.status === 'failed' ? 'bg-red-600' :
                    'bg-indigo-600 animate-pulse'
                  }`}>{(broadcastJobStatus.status || '').toUpperCase()}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div><p className="text-gray-400 text-xs">Total</p><p className="text-white font-bold">{broadcastJobStatus.total || '—'}</p></div>
                  <div><p className="text-gray-400 text-xs">Sent</p><p className="text-green-400 font-bold">{broadcastJobStatus.sent || 0}</p></div>
                  <div><p className="text-gray-400 text-xs">Failed</p><p className="text-red-400 font-bold">{broadcastJobStatus.failed || 0}</p></div>
                  <div><p className="text-gray-400 text-xs">Processed</p><p className="text-blue-400 font-bold">{broadcastJobStatus.processed || 0}</p></div>
                </div>
                {broadcastJobStatus.total > 0 && ['processing', 'claimed'].includes(broadcastJobStatus.status) && (
                  <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 transition-all duration-500"
                      style={{ width: `${Math.round(((broadcastJobStatus.processed || 0) / broadcastJobStatus.total) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

          </CardContent>
        </Card>

        {/* Students Table */}
        <Card className="bg-white/10 border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              All Students {students.length > 0 ? `(${students.length} results)` : '— Search below'}
              {students.filter(s => s.is_active_today).length > 0 && (
                <Badge className="bg-green-500 text-white ml-2">
                  {students.filter(s => s.is_active_today).length} Online
                </Badge>
              )}
            </CardTitle>
            <div className="mt-3">
              <input
                type="text"
                placeholder="Search by name, mobile or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full md:w-96 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                data-testid="admin-search-input"
              />
              {searchQuery && (
                <span className="text-sm text-gray-400 ml-3">{filteredStudents.length} results</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-white">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left p-3 text-gray-400 font-medium">Status</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Name</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Mobile</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Email</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Plan</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Cred Sent</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Questions</th>
                    <th className="text-left p-3 text-gray-400 font-medium">MCQ</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Credits</th>
                    <th className="text-left p-3 text-gray-400 font-medium">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student, idx) => (
                    <tr key={student.id || idx} className={`border-b border-white/10 hover:bg-white/5 ${student.is_active_today ? 'bg-green-500/5' : ''}`}>
                      <td className="p-3">
                        {getActivityBadge(student.is_active_today)}
                      </td>
                      <td className="p-3 font-medium">
                        <div>
                          {student.name || "-"}
                          {student.activity_7d > 0 && (
                            <span className="text-xs text-gray-400 block">
                              {student.activity_7d} activities (7d)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{student.mobile || "-"}</td>
                      <td className="p-3 text-sm text-gray-300 max-w-[200px]">
                        {editingEmailUserId === student.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="email"
                              value={newEmail}
                              onChange={(e) => setNewEmail(e.target.value)}
                              className="h-7 text-xs bg-white/10 border-white/30 text-white w-32"
                              placeholder="Enter email"
                              data-testid={`email-input-${student.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleEmailUpdate(student.id)}
                              disabled={emailUpdateLoading}
                              className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700"
                              data-testid={`email-save-${student.id}`}
                            >
                              <CheckCircle className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleEmailCancel}
                              className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/20"
                              data-testid={`email-cancel-${student.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="truncate" title={student.email || ""}>{student.email || "-"}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEmailEdit(student.id, student.email)}
                              className="h-6 w-6 p-0 text-blue-400 hover:bg-blue-500/20"
                              data-testid={`email-edit-${student.id}`}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="p-3">{getPlanBadge(student.plan)}</td>
                      <td className="p-3">
                        {student.welcome_email_sent 
                          ? <Badge className="bg-green-600/20 text-green-400 border-green-500/30"><Mail className="w-3 h-3 mr-1" />Sent</Badge>
                          : <Badge variant="outline" className="border-gray-600 text-gray-500">No</Badge>
                        }
                      </td>
                      <td className="p-3">
                        {student.questions_limit === -1 
                          ? <span className="text-yellow-400">∞</span>
                          : `${student.questions_used || 0} / ${student.questions_limit || 1}`
                        }
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="border-orange-500/50 text-orange-400">
                          {student.mcq_attempts || 0}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3 text-yellow-400" />
                          <span className="text-yellow-400">{student.estimated_credits || 0}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Clock className="w-4 h-4" />
                          {student.last_active 
                            ? formatDistanceToNow(new Date(student.last_active), { addSuffix: true })
                            : "Never"
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredStudents.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  {searchQuery.trim().length >= 2
                    ? <p>No students found for "<strong>{searchQuery}</strong>"</p>
                    : <div>
                        <p className="text-lg font-medium text-gray-300 mb-1">Search to view students</p>
                        <p className="text-sm">Type a name, email or mobile number above (min 2 characters)</p>
                        <p className="text-xs mt-2 text-gray-500">Students are loaded on-demand to keep the admin panel fast</p>
                      </div>
                  }
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
